import { cloneElement, useState } from "react";
import Button from "@mui/material/Button";
import { Card, CardContent } from "@mui/material";
import { ViewSidebar } from "@mui/icons-material";
import {
  AutocompleteInput,
  Datagrid,
  Filter,
  FilterList,
  FilterListItem,
  List,
  NumberField,
  ReferenceField,
  ReferenceInput,
  SelectInput,
  TextField,
  TextInput,
  TopToolbar,
} from "react-admin";
import {
  endOfMonth,
  endOfToday,
  endOfWeek,
  startOfMonth,
  startOfToday,
  startOfWeek,
} from "date-fns";
import { LocalizedDateField } from "../../components/localized-date-field.component";
import { CreateOrderDialog } from "../../components/create-order-dialog.component";

const paymentMethodChoices = [
  { id: "Pix", name: "Pix" },
  { id: "Depósito em conta", name: "Depósito em conta" },
  { id: "Cartão de Crédito", name: "Cartão de Crédito" },
  { id: "Cartão de Débito", name: "Cartão de Débito" },
  { id: "Boleto", name: "Boleto" },
  { id: "Cheque", name: "Cheque" },
  { id: "Empenho", name: "Empenho" },
];

const statusChoices = [
  { id: "Pagamento pendente", name: "Pagamento pendente" },
  { id: "Aguardando", name: "Aguardando" },
  { id: "Concluído", name: "Concluído" },
  { id: "Cancelado", name: "Cancelado" },
  { id: "Reembolsado", name: "Reembolsado" },
  { id: "Malsucedido", name: "Malsucedido" },
];

const OrdersFilter = (props: any) => (
  <Filter {...props}>
    <TextInput label="Transação" source="transactionId" alwaysOn />
    <TextInput label="Rastreamento" source="trackingCode" />
    <ReferenceInput
      label="Buscar por usuário"
      source="userId"
      reference="users"
      sort={{ field: "name", order: "ASC" }}
      filterToQuery={(searchText: string) => ({ name: searchText })}
      alwaysOn
    >
      <AutocompleteInput
        optionText={(choice: any) => {
          if (!choice) return "";
          const registration = choice.registration ? ` - ${choice.registration}` : "";
          return `${choice.name ?? ""}${registration}`;
        }}
        shouldRenderSuggestions={(value: string) => value.trim().length > 1}
        noOptionsText="Digite ao menos 2 caracteres para buscar usuários"
      />
    </ReferenceInput>
    <SelectInput label="Status" source="status" choices={statusChoices} />
    <SelectInput label="Pagamento" source="paymentMethod" choices={paymentMethodChoices} />
    <TextInput label="Cidade cobrança" source="billingCity" />
    <TextInput label="UF cobrança" source="billingState" />
    <TextInput label="Nota fiscal" source="invoice" />
  </Filter>
);

const OrdersSidebar = () => (
  <Card style={{ width: 250, marginRight: 16, order: -1 }}>
    <CardContent>
      <FilterList label="Criado" icon={null}>
        <FilterListItem
          label="Hoje"
          value={{
            createdAtStart: startOfToday().toISOString(),
            createdAtEnd: endOfToday().toISOString(),
          }}
        />
        <FilterListItem
          label="Esta semana"
          value={{
            createdAtStart: startOfWeek(new Date()).toISOString(),
            createdAtEnd: endOfWeek(new Date()).toISOString(),
          }}
        />
        <FilterListItem
          label="Este mês"
          value={{
            createdAtStart: startOfMonth(new Date()).toISOString(),
            createdAtEnd: endOfMonth(new Date()).toISOString(),
          }}
        />
      </FilterList>
      <FilterList label="Pedido" icon={null}>
        <FilterListItem
          label="Hoje"
          value={{
            orderDateStart: startOfToday().toISOString(),
            orderDateEnd: endOfToday().toISOString(),
          }}
        />
        <FilterListItem
          label="Esta semana"
          value={{
            orderDateStart: startOfWeek(new Date()).toISOString(),
            orderDateEnd: endOfWeek(new Date()).toISOString(),
          }}
        />
        <FilterListItem
          label="Este mês"
          value={{
            orderDateStart: startOfMonth(new Date()).toISOString(),
            orderDateEnd: endOfMonth(new Date()).toISOString(),
          }}
        />
      </FilterList>
    </CardContent>
  </Card>
);

const OrderListActions = (props: any) => {
  const { className, showSidebar, setShowSidebar, setOpenCreateDialog } = props;

  return (
    <TopToolbar className={className}>
      {cloneElement(props.filters, { context: "button" })}
      <Button color="primary" onClick={() => setShowSidebar(!showSidebar)}>
        <ViewSidebar style={{ marginRight: 8 }} />
        {showSidebar ? "Ocultar" : "Mostrar"} Filtros
      </Button>
      <Button color="primary" variant="contained" onClick={() => setOpenCreateDialog(true)}>
        Novo pedido
      </Button>
    </TopToolbar>
  );
};

export const OrderList = (props: any) => {
  const [showSidebar, setShowSidebar] = useState(true);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);

  return (
    <>
      <List
        {...props}
        actions={
          <OrderListActions
            showSidebar={showSidebar}
            setShowSidebar={setShowSidebar}
            openCreateDialog={openCreateDialog}
            setOpenCreateDialog={setOpenCreateDialog}
          />
        }
        aside={showSidebar ? <OrdersSidebar /> : null}
        filters={<OrdersFilter />}
        perPage={25}
        sort={{ field: "createdAt", order: "DESC" }}
      >
        <Datagrid rowClick="edit">
        <TextField source="id" label="ID" />
        <ReferenceField source="userId" reference="users" label="Usuário" sortable={false}>
          <TextField source="name" />
        </ReferenceField>
        <ReferenceField source="userId" reference="users" label="CPF/CNPJ" sortable={false}>
          <TextField source="registration" />
        </ReferenceField>
        <LocalizedDateField source="orderDate" label="Data pedido" showTime={false} />
        <LocalizedDateField source="receiptDate" label="Data recebimento" showTime={false} emptyText="Não recebida" />
        <TextField source="paymentMethod" label="Pagamento" />
        <TextField source="status" label="Status" />
        <TextField source="transactionId" label="Transação" />
        <TextField source="trackingCode" label="Rastreamento" />
        <NumberField source="totalDiscount" label="Desconto" options={{ style: "currency", currency: "BRL" }} />
        <NumberField source="totalAmount" label="Valor total" options={{ style: "currency", currency: "BRL" }} />
        <NumberField source="totalParcels" label="Parcelas" />
        <TextField source="billingCity" label="Cidade cobrança" />
        <TextField source="billingState" label="UF" />
        <LocalizedDateField source="createdAt" label="Criado em" showTime={false} />
        </Datagrid>
      </List>
      <CreateOrderDialog open={openCreateDialog} onClose={() => setOpenCreateDialog(false)} />
    </>
  );
};
